import { useState } from "react";
import {
  ArrowRightLeft,
  House,
  Mail,
  PieChart,
  LogOut,
  Instagram,
  Megaphone,
  Menu,
  X,
  ScanFace,
  LineChartIcon,
  Cog,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOutUser } from "services/auth.service";
import { useSite } from "providers/site.provider";

export default function SideMenu() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedSite, clearSite } = useSite();
  const [isOpen, setIsOpen] = useState(false);

  const base = "flex items-center gap-2 rounded-md px-3 py-2 text-sm";
  const active = " bg-accent";
  const hover = " hover:bg-accent";

  const isWebsites = pathname.startsWith("/websites");
  const isDashboard = pathname.startsWith("/dashboard");
  const isReports = pathname.startsWith("/reports");
  const lastSiteId =
    typeof window !== "undefined" ? localStorage.getItem("lastSiteId") : null;
  const isAddWebsite = pathname.startsWith("/websites/new");
  const isSocial = pathname.startsWith("/social");
  const isMaintenance = pathname.startsWith("/maintenance");

  return (
    <>
      {/* Hamburger button on mobile */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-gray-100/30 rounded shadow"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-60 bg-card border-r p-4 flex flex-col z-40
          transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:relative md:h-full
        `}
      >
        <div>
          <div className="mb-6 text-sm font-semibold uppercase text-muted-foreground">
            APF Reporting
          </div>
          <nav className="space-y-1">
            {isAddWebsite ? (
              lastSiteId ? (
                <Link
                  to="/websites"
                  className={base + (isDashboard ? active : hover)}
                >
                  <House className="h-4 w-4" /> Home
                </Link>
              ) : (
                <Link
                  to="/websites"
                  className={base + (isDashboard ? active : hover)}
                >
                  <PieChart className="h-4 w-4" /> Dashboard
                </Link>
              )
            ) : (
              <>
                <Link
                  to="/websites"
                  className={base + (isWebsites ? active : hover)}
                >
                  <ArrowRightLeft className="h-4 w-4" /> Switch site
                </Link>
                {JSON.stringify(selectedSite?.integrations) !==
                  JSON.stringify(["instagram"]) && (
                  <Link
                    to={`/dashboard/`}
                    className={base + (isDashboard ? active : hover)}
                  >
                    <PieChart className="h-4 w-4" /> Summary
                  </Link>
                )}
                <Link
                  to={`/maintenance/`}
                  className={base + (isMaintenance ? active : hover)}
                >
                  <Cog className="h-4 w-4" /> Maintenance
                </Link>
                <Link
                  to={`/social/`}
                  className={base + (isSocial ? active : hover)}
                >
                  <Instagram className="h-4 w-4" /> Social
                </Link>
                <Link
                  to={`/seo/`}
                  className={base + (isReports ? active : hover)}
                >
                  <LineChartIcon className="h-4 w-4" /> SEO
                </Link>

                <Link
                  to={`/feedback/`}
                  className={base + (isReports ? active : hover)}
                >
                  <Megaphone className="h-4 w-4" /> Feedback
                </Link>

                <Link
                  to={`/account/`}
                  className={base + (isReports ? active : hover)}
                >
                  <ScanFace className="h-4 w-4" /> Account
                </Link>
              </>
            )}
          </nav>
        </div>

        <div className="mt-auto flex flex-col gap-2 rounded-md px-3 py-2">
          <a
            href="mailto:hello@apfome.com"
            className="flex items-center gap-2 text-sm"
          >
            <Mail className="h-4 w-4" /> Contact Support
          </a>

          <button
            className="flex items-center gap-2 text-red-600 hover:bg-red-100 py-1 rounded text-sm"
            onClick={async () => {
              const signout = await signOutUser();
              if (signout.success) {
                navigate("/login");
              }
            }}
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Overlay when sidebar is open on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
